import { z } from 'zod';
import { AdsEntityId } from './ads-campaigns.js';
/*
 * Правка живых кампаний и групп (S2, `docs/live-edit-2026-11-04/spec-s2-live-edit.md` §1.3–1.10, §5.1):
 * чтение настроек, предпросмотр и запись бюджета / ставки / таргетинга. Деньги — минорные единицы
 * валюты кабинета (`*Minor`). Одна правка = один вид (`kind`). В Meta уходят только числа, коды
 * стран, id интересов и аудиторий — поэтому эти поля не принимают кириллицу; `reason` (аудит,
 * в Meta не уходит) — свободный русский текст. Разбор ответов мягкий (`catch`): auto-ads пишется
 * параллельно.
 */
export const ADS_LIVE_REASON_MAX = 500;
export const ADS_LIVE_ACKNOWLEDGE_CODES = ['rule-episode-will-close', 'budget-big-step'];
export const ADS_LIVE_KINDS = ['budget', 'bid', 'targeting'];
export const AdsLiveKind = z.enum(ADS_LIVE_KINDS);
export const ADS_LIVE_BID_STRATEGIES = ['BID_CAP', 'COST_CAP', 'HIGHEST_VOLUME', 'ROAS'];
export const AdsLiveBidStrategy = z.enum(ADS_LIVE_BID_STRATEGIES);
export const AdsLiveLevel = z.enum(['campaign', 'adset']);
const iso2 = z
    .string()
    .regex(/^[A-Z]{2}$/, { message: 'Код страны — две латинские заглавные буквы' });
const numericId = z.string().regex(/^\d{1,30}$/, { message: 'Нужен числовой id' });
const sha = z.string().length(64);
// ---- тело правки (зеркало 1.4 без dryRun) -----------------------------------------------------
export const AdsLiveGeo = z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('WORLDWIDE') }).strict(),
    z.object({ mode: z.literal('COUNTRIES'), countries: z.array(iso2).min(1).max(250) }).strict(),
]);
const BudgetBody = z
    .object({
    kind: z.literal('budget'),
    budgetType: z.enum(['daily', 'lifetime']),
    amountMinor: z.number().int().positive(),
})
    .strict();
const BidBody = z
    .object({
    kind: z.literal('bid'),
    bidStrategy: AdsLiveBidStrategy.optional(),
    bidAmountMinor: z.number().int().min(100).max(100_000).optional(),
    roasFloor: z.number().min(0.01).max(1000).optional(),
})
    .strict()
    .refine((b) => b.bidStrategy !== undefined || b.bidAmountMinor !== undefined || b.roasFloor !== undefined, {
    message: 'Нужно хотя бы одно поле правки',
});
const TargetingBody = z
    .object({
    kind: z.literal('targeting'),
    geo: AdsLiveGeo.optional(),
    excludedCountries: z.array(iso2).max(250).nullable().optional(),
    age: z
        .object({ min: z.number().int().min(18).max(65), max: z.number().int().min(18).max(65) })
        .strict()
        .optional(),
    gender: z.union([z.literal(0), z.literal(1), z.literal(2)]).optional(),
    interests: z
        .array(z.object({ id: numericId }).strict())
        .max(25)
        .optional(),
    customAudienceIds: z.array(numericId).max(50).optional(),
    excludedAudienceIds: z.array(numericId).max(50).optional(),
    advantageAudience: z.boolean().optional(),
})
    .strict()
    .refine((b) => Object.keys(b).some((k) => k !== 'kind' && b[k] !== undefined), { message: 'Нужно хотя бы одно поле правки' });
export const AdsSettingsBody = z.union([BudgetBody, BidBody, TargetingBody]);
const Reason = z.string().trim().max(ADS_LIVE_REASON_MAX).optional();
export const AdsLiveExpected = z
    .object({ baselineHash: sha, diffHash: sha, previewId: z.string().regex(/^[a-f0-9]{32}$/) })
    .strict();
/** Предпросмотр: тело правки + причина. */
export const AdsSettingsPreviewRequest = z.object({
    change: AdsSettingsBody,
    reason: Reason,
});
/** Запись: то же + ключ идемпотентности, `expected` из предпросмотра и подтверждённые предупреждения. */
export const AdsSettingsApplyRequest = z.object({
    change: AdsSettingsBody,
    reason: Reason,
    idempotencyKey: z.string().regex(/^[A-Za-z0-9_-]{16,64}$/),
    expected: AdsLiveExpected,
    acknowledge: z.array(z.enum(ADS_LIVE_ACKNOWLEDGE_CODES)).max(2).optional(),
});
/** Параметры пути маршрутов правки. */
export const AdsLiveTargetParams = z.object({ id: z.string(), entityId: AdsEntityId });
// ---- чтение -----------------------------------------------------------------------------------
const nstr = z
    .string()
    .nullish()
    .transform((v) => v ?? null);
const nnum = z
    .number()
    .nullish()
    .transform((v) => v ?? null);
export const AdsLiveThrottle = z
    .object({
    max: z.number().catch(0),
    used: z.number().catch(0),
    remaining: nnum,
    nextAllowedAt: nstr,
})
    .nullish()
    .transform((v) => v ?? null);
export const AdsLiveBudget = z.object({
    editable: z.boolean().catch(false),
    blockedReason: nstr,
    blockedText: nstr,
    type: z
        .enum(['daily', 'lifetime'])
        .nullish()
        .transform((v) => v ?? null),
    dailyMinor: nnum,
    lifetimeMinor: nnum,
    endTime: nstr,
    minimums: z
        .object({ dailyMinor: nnum, lifetimeMinor: nnum })
        .catch({ dailyMinor: null, lifetimeMinor: null }),
    throttle: AdsLiveThrottle,
});
const strategyOrNull = AdsLiveBidStrategy.nullish()
    .transform((v) => v ?? null)
    .catch(null);
export const AdsLiveAudienceRef = z.object({ id: z.string(), name: nstr, slot: nstr });
export const AdsLiveActiveEpisode = z
    .object({
    ruleId: z.string(),
    ruleName: z.string().catch(''),
    baselineMinor: z.number(),
    appliedMinor: z.number(),
    startedDay: z.string().catch(''),
    restoreDueAt: nstr,
})
    .nullish()
    .transform((v) => v ?? null);
export const AdsLiveOrigin = z
    .object({ kind: z.literal('launch'), launchId: z.string(), startedAt: nstr })
    .nullish()
    .transform((v) => v ?? null)
    .catch(null);
const Currency = z.object({ code: z.string().catch('USD'), offset: z.number().catch(100) }).catch({
    code: 'USD',
    offset: 100,
});
const LiveCommon = {
    entityId: z.string(),
    name: z.string().catch(''),
    status: z.string().catch(''),
    effectiveStatus: z.string().catch(''),
    updatedTime: nstr,
    readAt: z.string().catch(''),
    baselineHash: z.string(),
    currency: Currency,
    budgetLevel: z.enum(['CBO', 'ABO', 'none']).catch('none'),
    budget: AdsLiveBudget,
    activeEpisode: AdsLiveActiveEpisode,
    origin: AdsLiveOrigin,
    /** Не удалось проверить, создан ли объект запуском (правка остаётся, но граница запуска не проверена). */
    originUnknown: z.boolean().catch(false),
    /** Не удалось проверить правила бюджета: активное снижение правилом могло остаться незамеченным. */
    activeEpisodeUnknown: z.boolean().catch(false),
    /** У кампании не прочитана часть групп — бюджет и ставка кампании не правятся. */
    adSetsIncomplete: z.boolean().catch(false),
};
export const AdsLiveSettingsAdSet = z.object({
    level: z.literal('adset'),
    ...LiveCommon,
    campaignId: z.string().catch(''),
    campaignName: z.string().catch(''),
    bid: z.object({
        editable: z.boolean().catch(false),
        blockedText: nstr,
        strategy: strategyOrNull,
        strategyMeta: nstr,
        strategyLevel: z.enum(['campaign', 'adset']).catch('adset'),
        amountMinor: nnum,
        roasFloor: nnum,
        optimization: z
            .enum(['OFFSITE_CONVERSIONS', 'VALUE', 'PLTV'])
            .nullish()
            .transform((v) => v ?? null)
            .catch(null),
    }),
    targeting: z.object({
        editable: z.boolean().catch(false),
        blockedText: nstr,
        /** `null` — страны не прочитались: правку таргетинга закрываем (не подставляем «Весь мир»). */
        geo: z
            .discriminatedUnion('mode', [
            z.object({ mode: z.literal('WORLDWIDE') }),
            z.object({ mode: z.literal('COUNTRIES'), countries: z.array(z.string()) }),
            // группы стран, регионы или города: страны отсюда не правятся (только показ)
            z.object({ mode: z.literal('NON_COUNTRY'), present: z.array(z.string()).catch([]) }),
        ])
            .nullable()
            .catch(null),
        /** Можно ли править страны: регионы и города в группе править отсюда нельзя. Нет поля — можно. */
        geoEditable: z.boolean().catch(true),
        geoBlockedText: nstr,
        /** Что именно задано, кроме стран (машинные ключи; людям не показываем). */
        geoNonCountry: z.array(z.string()).catch([]),
        excludedCountries: z.array(z.string()).catch([]),
        /** `null` — возраст не прочитался. */
        age: z.object({ min: z.number(), max: z.number() }).nullable().catch(null),
        /** `null` — пол не прочитался. */
        gender: z
            .union([z.literal(0), z.literal(1), z.literal(2)])
            .nullable()
            .catch(null),
        interests: z.array(z.object({ id: z.string(), name: z.string().catch('') })).catch([]),
        /** Интересы правятся, пока в группе не больше одной группы условий. Нет поля — можно. */
        interestsEditable: z.boolean().catch(true),
        /** Все группы условий (интересы и поведения) — только для показа. */
        flexibleSpec: z
            .array(z.object({
            interests: z.array(z.object({ id: z.string(), name: z.string().catch('') })).catch([]),
            behaviors: z.array(z.object({ id: z.string(), name: z.string().catch('') })).catch([]),
            otherKeys: z.array(z.string()).catch([]),
        }))
            .catch([]),
        customAudiences: z.array(AdsLiveAudienceRef).catch([]),
        excludedAudiences: z.array(AdsLiveAudienceRef).catch([]),
        advantageAudience: z.boolean().catch(false),
        /** Явно ли задан Advantage+ в Meta; `false` — форма обязана спросить человека. Нет поля — известно. */
        advantageAudienceKnown: z.boolean().catch(true),
        readOnly: z
            .object({
            locales: z.array(z.number()).catch([]),
            placementsText: nstr,
            otherKeys: z.array(z.string()).catch([]),
        })
            .catch({ locales: [], placementsText: null, otherKeys: [] }),
    }),
    options: z
        .object({
        audiences: z.array(AdsLiveAudienceRef).catch([]),
        countryLists: z
            .array(z.object({ id: z.string(), name: z.string(), countries: z.array(z.string()) }))
            .catch([]),
    })
        .catch({ audiences: [], countryLists: [] }),
    learning: z.object({ status: nstr }).catch({ status: null }),
});
export const AdsLiveSettingsCampaign = z.object({
    level: z.literal('campaign'),
    ...LiveCommon,
    bid: z.object({
        editable: z.boolean().catch(false),
        blockedText: nstr,
        strategy: strategyOrNull,
        strategyMeta: nstr,
        strategyLevel: z.literal('campaign').catch('campaign'),
    }),
    adSets: z
        .array(z.object({
        id: z.string(),
        name: z.string().catch(''),
        status: z.string().catch(''),
        effectiveStatus: z.string().catch(''),
        budget: z
            .object({
            type: z
                .enum(['daily', 'lifetime'])
                .nullish()
                .transform((v) => v ?? null),
            dailyMinor: nnum,
            lifetimeMinor: nnum,
        })
            .catch({ type: null, dailyMinor: null, lifetimeMinor: null }),
        bid: z
            .object({ strategy: strategyOrNull, amountMinor: nnum })
            .catch({ strategy: null, amountMinor: null }),
        learning: z.object({ status: nstr }).catch({ status: null }),
        throttle: AdsLiveThrottle,
    }))
        .catch([]),
});
export const AdsLiveSettings = z.discriminatedUnion('level', [
    AdsLiveSettingsAdSet,
    AdsLiveSettingsCampaign,
]);
// ---- предпросмотр и результат -----------------------------------------------------------------
export const AdsChange = z.object({
    field: z.string(),
    label: z.string().catch(''),
    before: z.string().catch(''),
    after: z.string().catch(''),
    beforeRaw: z.unknown().optional(),
    afterRaw: z.unknown().optional(),
    added: z.array(z.string()).optional().catch(undefined),
    removed: z.array(z.string()).optional().catch(undefined),
});
export const AdsSettingsWarning = z.object({
    code: z.string(),
    severity: z.enum(['info', 'warning']).catch('warning'),
    requiresAck: z.boolean().catch(false),
    message: z.string().catch(''),
    details: z.record(z.string(), z.unknown()).optional().catch(undefined),
});
export const AdsSettingsIssue = z.object({
    fieldKey: nstr,
    code: z.string(),
    severity: z.enum(['error', 'warning', 'info']).catch('warning'),
    message: z.string().catch(''),
});
export const AdsSettingsPreview = z.object({
    level: AdsLiveLevel,
    entityId: z.string(),
    campaignId: z.string().catch(''),
    kind: AdsLiveKind,
    previewId: z.string(),
    noop: z.boolean().catch(false),
    changes: z.array(AdsChange).catch([]),
    warnings: z.array(AdsSettingsWarning).catch([]),
    issues: z.array(AdsSettingsIssue).catch([]),
    baselineHash: z.string(),
    diffHash: z.string(),
    throttle: AdsLiveThrottle,
    readAt: z.string().catch(''),
});
export const AdsSettingsResult = z.object({
    result: z.enum(['applied', 'noop', 'repeated']),
    changeId: z.string().optional().catch(undefined),
    level: AdsLiveLevel,
    entityId: z.string(),
    campaignId: z.string().catch(''),
    kind: AdsLiveKind,
    changes: z.array(AdsChange).catch([]),
    warnings: z.array(AdsSettingsWarning).catch([]),
    readBack: z
        .object({
        ok: z.boolean().catch(true),
        /** `false` — сверка не выполнилась: результат не подтверждён. Нет поля — подтверждён. */
        verified: z.boolean().catch(true),
        mismatches: z
            .array(z.object({
            field: z.string(),
            sent: z.unknown().optional(),
            got: z.unknown().optional(),
        }))
            .catch([]),
        notes: z.array(z.string()).optional().catch(undefined),
    })
        .catch({ ok: true, verified: true, mismatches: [] }),
    syncWarning: nstr,
    throttle: AdsLiveThrottle,
});
//# sourceMappingURL=ads-live-edit.js.map